"use client";
import React, { useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import { MotionDiv, MotionSection, Variants } from "@/utils/clientMotion";
import StatsCard from "@/components/stats-card";
import { statsContainer, statsItem } from "../app/variants/statsVariants";

interface Stat {
  img: string;
  alt: string;
  count: string;
  title: string;
  client: string;
}

type DashboardSummary = {
  totalDeposit: {
    total: number;
    byType: { itemType: string; quantity: number }[];
  };
  treeCount: number | string;
  totalCfKgCO2e: number | string;
};

export default function ReBottStats() {
  const DASHBOARD_API_ENDPOINT = "/api/rebott-dashboard-summary";
  // กำหนดวันที่ตั้งแต่ (คงที่)
  const startDate = "01/01/2025";

  // เก็บวันที่อัปเดต (เมื่อวานนี้)
  const [updateDate, setUpdateDate] = useState("");

  const [stats, setStats] = useState<Stat[]>([
    {
      img: "/image/stat1.png",
      alt: "จำนวนขวดทั้งหมดที่ได้คืนจากตู้รับคืนขวด",
      count: "0",
      title: "จำนวนขวดทั้งหมด\nที่ได้คืนจากตู้รับคืนขวด",
      client: "(ขวด)",
    },
    {
      img: "/image/stat2.png",
      alt: "จำนวนต้นไม้ที่เราช่วยรักษาไว้ได้จากการรีไซเคิลขวดที่ได้รับคืน",
      count: "0",
      title:
        "จำนวนต้นไม้ที่เราช่วยรักษาไว้ได้\nจากการรีไซเคิลขวดที่ได้รับคืน",
      client: "(ต้น)",
    },
    {
      img: "/image/stat3.png",
      alt: "ปริมาณ CO2 ที่ลดลง",
      count: "0",
      title: "ปริมาณ CO2 ที่ลดลง\nโดยคำนวณจากขวดที่ได้รับคืน",
      client: "(Kg.co2)",
    },
  ]);

  // คำนวณ “อัปเดตล่าสุด: เมื่อวาน”
  useEffect(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    setUpdateDate(`${dd}/${mm}/${yyyy}`);
  }, []);

  // Header variants: ข้อความเลื่อนลงมา
  const headerVariants: Variants = {
    hidden: { y: -50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  // 🔁 ดึงข้อมูลสรุปจาก API เดียว
  useEffect(() => {
    fetch(DASHBOARD_API_ENDPOINT, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) {
          return {
            totalDeposit: { total: 0, byType: [] },
            treeCount: 0,
            totalCfKgCO2e: 0,
          } as DashboardSummary;
        }
        return res.json() as Promise<DashboardSummary>;
      })
      .then((data) => {
        const totalBottle = Number(data.totalDeposit.total) || 0;
        const treeCount = Number(data.treeCount) || 0;
        const totalCfKgCO2e = Number(data.totalCfKgCO2e) || 0;

        setStats((prev) => [
          {
            ...prev[0],
            count: totalBottle.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            }),
          },
          {
            ...prev[1],
            count: treeCount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          },
          {
            ...prev[2],
            count: totalCfKgCO2e.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          },
        ]);
      })
      .catch(() => {
        // keep default 0 values on error
      });
  }, []);

  return (
    <MotionSection
      className="px-10 py-20"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.4 }}
      variants={statsContainer}
    >
      <div className="container mx-auto">
        <MotionDiv
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={headerVariants}
          className="container mx-auto text-center mb-12"
        >
          <Typography {...({} as any)} variant="h3" className="mb-1 text-[1.6rem] md:text-[30px] text-gray-800">
            Reverse Vending Machine,<br className="block md:hidden" /> Renew the Future
          </Typography>
          <Typography {...({} as any)} variant="lead" className="mx-auto mb-[6px] w-full lg:w-5/12 text-[#2998E2] text-[1.2rem]">
            เพื่อสิ่งแวดล้อม ชุมชน และสังคมที่ยั่งยืน
          </Typography>
          <div className="inline-block bg-[#2998E2] text-white text-sm px-4 py-1 rounded-lg">
            สถิติสะสมตั้งแต่: {startDate} <br className="block md:hidden" />
            อัพเดตล่าสุด: {updateDate}
          </div>
        </MotionDiv>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-12 lg:px-12">
          {stats.map((stat, idx) => (
            <MotionDiv
              key={idx}
              variants={statsItem}
              className="bg-[#eff6ff] rounded-[20px] pt-[2.7rem] pb-[1.7rem] px-9 flex flex-col items-center transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ cursor: "pointer" }}
            >
              <StatsCard {...stat} />
            </MotionDiv>
          ))}
        </div>
      </div>
    </MotionSection>
  );
}

